import { makeAutoObservable } from 'mobx';
import { ServiceModel, Service } from '../../../model/professional/service-model';
import { collaboratorsByCompany } from '../../../model/professional/compagny-model';
import toastStore from '../../../store/toast-store';
import serviceStore from '../../../store/service-store';
import professionalStore from '../../../store/professional-store';
import companyStore from '../../../store/company-store';
import subscriptionStore from '../../../store/subscription-store';

class ServiceFormViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  imagePreview: string | null = null;
  imageFile: File | null = null;
  imageRemoved: boolean = false;
  requiresBooking: boolean = true;
  selectedCollaboratorIds: string[] = [];
  snackbarState: {
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  } = {
    open: false,
    message: '',
    severity: 'error'
  };

  // Configuration des acomptes
  depositRequired: boolean = false;
  depositType: 'percentage' | 'fixed' = 'percentage';
  depositValue: number | null = null;

  // Taille maximale de l'image en octets (2MB)
  readonly MAX_IMAGE_SIZE = 2 * 1024 * 1024;

  private serviceModel: ServiceModel;

  constructor() {
    makeAutoObservable(this);
    this.serviceModel = ServiceModel.getInstance();
  }

  /**
   * Gère les états de chargement de manière centralisée
   */
  setLoading(value: boolean): void {
    this.isLoading = value;
  }

  setError(value: string | null): void {
    this.error = value;
  }

  setImagePreview(value: string | null): void {
    this.imagePreview = value;
  }

  setImageFile(file: File | null): void {
    this.imageFile = file;
  }

  setImageRemoved(value: boolean): void {
    this.imageRemoved = value;
  }

  setRequiresBooking(value: boolean): void {
    this.requiresBooking = value;
  }

  setSelectedCollaboratorIds(ids: string[]): void {
    this.selectedCollaboratorIds = ids;
  }

  setDepositRequired(value: boolean): void {
    this.depositRequired = value;
  }

  setDepositType(value: 'percentage' | 'fixed'): void {
    this.depositType = value;
  }

  setDepositValue(value: number | null): void {
    this.depositValue = value;
  }

  toggleCollaborator(collaboratorId: string): void {
    const index = this.selectedCollaboratorIds.indexOf(collaboratorId);
    if (index > -1) {
      this.selectedCollaboratorIds = this.selectedCollaboratorIds.filter(id => id !== collaboratorId);
    } else {
      this.selectedCollaboratorIds = [...this.selectedCollaboratorIds, collaboratorId];
    }
  }

  showSnackbar(message: string, severity: 'error' | 'warning' | 'info' | 'success'): void {
    this.snackbarState = {
      open: true,
      message,
      severity
    };
  }

  closeSnackbar(): void {
    this.snackbarState = {
      ...this.snackbarState,
      open: false
    };
  }

  /**
   * Initialise le formulaire avec les données d'un service existant
   */
  initializeForm(service: Service | null): void {
    if (service) {
      // Mode édition : charger les données du service existant
      this.setImagePreview(service.image || null);
      this.setRequiresBooking(service.requiresBooking ?? true);

      // Initialiser les valeurs d'acompte
      this.setDepositRequired(service.depositRequired ?? false);
      this.setDepositType(service.depositType || 'percentage');
      this.setDepositValue(service.depositValue ?? null);

      // Extraire les IDs des collaborateurs depuis service.collaboratorIds ou service.collaborators
      const collaboratorIds = service.collaboratorIds ||
                             (service as any).collaborators?.map((c: any) => c.collaboratorCompagnieId) ||
                             [];

      this.setSelectedCollaboratorIds(collaboratorIds);
    } else {
      // Mode création : réinitialiser et pré-sélectionner l'owner
      this.resetForm();
      this.preselectOwner();
    }
  }

  /**
   * Pré-sélectionne automatiquement l'owner lors de la création d'un nouveau service
   * L'owner peut se désélectionner manuellement s'il ne souhaite pas effectuer cette prestation
   */
  private preselectOwner(): void {
    const ownerId = professionalStore.getId;
    const collaborators = companyStore.getDefaultCompanyCollaborators;

    if (!ownerId || collaborators.length === 0) {
      return;
    }

    // Trouver le collaborator_compagnies.id correspondant au collaboratorId (user ID)
    const ownerCollaborator = collaborators.find(
      collab => collab.collaboratorId === ownerId
    );

    if (ownerCollaborator) {
      // Pré-sélectionner avec l'ID de collaborator_compagnies (cc.id)
      this.setSelectedCollaboratorIds([ownerCollaborator.collaboratorCompagnieId]);
    }
  }

  /**
   * Réinitialise le formulaire
   */
  resetForm(): void {
    this.setImagePreview(null);
    this.setImageFile(null);
    this.setImageRemoved(false);
    this.setRequiresBooking(true);
    this.setSelectedCollaboratorIds([]);
    this.setError(null);
    this.setDepositRequired(false);
    this.setDepositType('percentage');
    this.setDepositValue(null);
  }

  /**
   * Gère le changement d'image
   */
  handleImageChange(file: File): void {
    // Vérifier la taille de l'image
    if (file.size > this.MAX_IMAGE_SIZE) {
      this.showSnackbar(
        `L'image est trop volumineuse. Taille maximale: 2MB. Taille actuelle: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
        'error'
      );
      return;
    }
    
    this.setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Supprime l'image sélectionnée
   */
  handleRemoveImage(): void {
    this.setImagePreview(null);
    this.setImageFile(null);
    this.setImageRemoved(true);
  }

  /**
   * Prépare les données du formulaire pour l'envoi
   */
  prepareFormData(formElement: HTMLFormElement): FormData {
    const formData = new FormData(formElement);

    // Ajouter le companyId de la company active
    const companyId = companyStore.getIdCompanySelectedDefault;
    if (companyId) {
      formData.append('companyId', companyId);
    }

    // Ajouter l'image si elle existe
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }

    // Convertir les valeurs booléennes
    const requiresBooking = formData.get('requiresBooking') === 'on';
    const isActive = formData.get('isActive') === 'on';
    const depositRequired = formData.get('depositRequired') === 'on';
    const minTimeBeforeBooking = formData.get('minTimeBeforeBooking') as string;
    const bufferTimeBetweenSlots = formData.get('bufferTimeBetweenSlots') as string;
    const depositType = formData.get('depositType') as string;
    const depositValue = formData.get('depositValue') as string;
    const cancellationRefundDeadlineHours = formData.get('cancellationRefundDeadlineHours') as string;

    formData.delete('requiresBooking');
    formData.delete('isActive');
    formData.delete('depositRequired');
    formData.delete('minTimeBeforeBooking');
    formData.delete('bufferTimeBetweenSlots');
    formData.delete('depositType');
    formData.delete('depositValue');
    formData.delete('cancellationRefundDeadlineHours');

    formData.append('requiresBooking', requiresBooking ? 'true' : 'false');
    formData.append('isActive', isActive ? 'true' : 'false');
    formData.append('depositRequired', depositRequired ? 'true' : 'false');

    // Convertir les valeurs vides en null pour les champs entiers
    formData.append('minTimeBeforeBooking', minTimeBeforeBooking && minTimeBeforeBooking.trim() !== '' ? minTimeBeforeBooking : 'null');
    formData.append('bufferTimeBetweenSlots', bufferTimeBetweenSlots && bufferTimeBetweenSlots.trim() !== '' ? bufferTimeBetweenSlots : 'null');

    // Ajouter le délai d'annulation (seulement si l'abonnement permet les acomptes)
    if (requiresBooking && subscriptionStore.hasFeature('depositPayment')) {
      formData.append('cancellationRefundDeadlineHours', cancellationRefundDeadlineHours && cancellationRefundDeadlineHours.trim() !== '' ? cancellationRefundDeadlineHours : '24');
    } else {
      formData.append('cancellationRefundDeadlineHours', 'null');
    }

    // Ajouter les champs d'acompte (seulement si acompte requis)
    if (depositRequired) {
      formData.append('depositType', depositType || 'percentage');
      formData.append('depositValue', depositValue && depositValue.trim() !== '' ? depositValue : 'null');
    } else {
      formData.append('depositType', 'null');
      formData.append('depositValue', 'null');
    }

    // Ajouter les collaborateurs sélectionnés
    if (this.selectedCollaboratorIds.length > 0) {
      formData.append('collaboratorIds', JSON.stringify(this.selectedCollaboratorIds));
    }

    // Si l'image a été supprimée, ajouter le champ removeImage au FormData
    if (this.imageRemoved) {
      formData.append('removeImage', 'true');
    }

    return formData;
  }

  /**
   * Valide les champs obligatoires du formulaire
   * @returns {boolean} true si tous les champs obligatoires sont remplis, false sinon
   */
  validateRequiredFields(formData: FormData): boolean {
    const name = formData.get('name') as string;
    const price = formData.get('price') as string;
    const requiresBooking = formData.get('requiresBooking') === 'true';
    const duration = formData.get('duration') as string;
    
    if (!name || name.trim() === '') {
      this.showSnackbar('Le nom du service est obligatoire', 'error');
      return false;
    }
    
    if (!price || isNaN(parseFloat(price))) {
      this.showSnackbar('Le prix du service est obligatoire et doit être un nombre valide', 'error');
      return false;
    }
    
    // Vérifier que le prix n'est pas 0
    if (parseFloat(price) <= 0) {
      this.showSnackbar('Le prix du service doit être supérieur à 0€', 'error');
      return false;
    }
    
    if (requiresBooking && (!duration || isNaN(parseInt(duration, 10)))) {
      this.showSnackbar('La durée est obligatoire pour un service avec réservation', 'error');
      return false;
    }
    
    return true;
  }

  /**
   * Soumet le formulaire pour créer un service
   */
  async submitCreateForm(formElement: HTMLFormElement): Promise<string | null> {
    this.setLoading(true);

    try {
      const formData = this.prepareFormData(formElement);

      // Valider les champs obligatoires
      if (!this.validateRequiredFields(formData)) {
        this.setLoading(false);
        return null;
      }

      const createdService = await this.serviceModel.createService(formData);

      if (!createdService.success || !createdService.data) {
        throw new Error(createdService.message || 'Impossible de créer le service');
      }

      const newService = createdService.data;

      // Synchroniser les collaborateurs du service via l'endpoint batch (1 seule requête)
      try {
        await this.serviceModel.syncCollaborators(newService.id, this.selectedCollaboratorIds);
      } catch (collabError) {
        console.error('Error syncing collaborators:', collabError);
        // On continue même si la synchronisation des collaborateurs échoue
      }

      // Transformer les catégories d'options pour correspondre au type CategoryWithOptions
      const formattedCategories = (newService.optionCategories || []).map(cat => ({
        ...cat,
        options: Array.isArray(cat.options) ? cat.options : []
      }));

      // Construire les collaborateurs à partir des IDs sélectionnés et des données du store
      const allCollaborators = companyStore.getDefaultCompanyCollaborators;
      const selectedCollaborators = this.selectedCollaboratorIds
        .map(id => allCollaborators.find(c => c.collaboratorCompagnieId === id))
        .filter((c): c is collaboratorsByCompany => c !== undefined);

      serviceStore.addService({
        ...newService,
        optionCategories: formattedCategories,
        collaborators: selectedCollaborators,
        collaboratorIds: this.selectedCollaboratorIds
      });

      toastStore.success(createdService.message || 'Service créé avec succès');
      return createdService.data.id;
    } catch (error: any) {
      console.error('Error creating service:', error);
      this.showSnackbar(error.message || 'Une erreur est survenue lors de la création du service', 'error');
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Soumet le formulaire pour mettre à jour un service
   */
  async submitUpdateForm(serviceId: string, formElement: HTMLFormElement): Promise<boolean> {
    this.setLoading(true);

    try {
      const formData = this.prepareFormData(formElement);

      // Valider les champs obligatoires
      if (!this.validateRequiredFields(formData)) {
        this.setLoading(false);
        return false;
      }

      const updatedService = await this.serviceModel.updateService(serviceId, formData);

      if (!updatedService.success) {
        throw new Error(updatedService.message || `Impossible de mettre à jour le service ${serviceId}`);
      }

      // Synchroniser les collaborateurs via l'endpoint batch (1 seule requête)
      try {
        await this.serviceModel.syncCollaborators(serviceId, this.selectedCollaboratorIds);
      } catch (collabError) {
        console.error('Error syncing collaborators:', collabError);
        // On continue même si la synchronisation des collaborateurs échoue
      }

      // Mettre à jour le service dans le store pour mise à jour immédiate de l'UI
      if (updatedService.data) {
        const updatedServiceData = updatedService.data;

        // Transformer les catégories d'options pour correspondre au type CategoryWithOptions
        const formattedCategories = (updatedServiceData.optionCategories || []).map(cat => ({
          ...cat,
          options: Array.isArray(cat.options) ? cat.options : []
        }));

        // Construire les collaborateurs à partir des IDs sélectionnés et des données du store
        const allCollaborators = companyStore.getDefaultCompanyCollaborators;
        const selectedCollaborators = this.selectedCollaboratorIds
          .map(id => allCollaborators.find(c => c.collaboratorCompagnieId === id))
          .filter((c): c is collaboratorsByCompany => c !== undefined);

        serviceStore.updateServiceInStore({
          ...updatedServiceData,
          optionCategories: formattedCategories,
          collaborators: selectedCollaborators,
          collaboratorIds: this.selectedCollaboratorIds
        });
      }

      toastStore.success(updatedService.message || 'Service mis à jour avec succès');
      return true;
    } catch (error: any) {
      console.error('Error updating service:', error);
      this.showSnackbar(error.message || 'Une erreur est survenue lors de la mise à jour du service', 'error');
      return false;
    } finally {
      this.setLoading(false);
    }
  }
}

// Singleton instance
const serviceFormViewModel = new ServiceFormViewModel();

// Hook pour utiliser le view model dans les composants React
export const useServiceFormViewModel = () => {
  return serviceFormViewModel;
};

export default serviceFormViewModel;
